import {
  codeToolSecretValueMaxLength,
  codeToolSecretValueMinLength,
  type CodeToolSecretListing,
  type CodeToolSecretsWrite,
} from "@repo/app-types/entities/tools/code";
import { codeToolSecretNameSchema } from "@repo/app-types/helpers/codeToolTurn";

export const secretDomainChangeNeedsValueMessage =
  "Re-enter the value to change where this secret can be sent.";

/** A secret as the form edits it. Values are write-only: none is ever read back. */
export interface CodeToolFormSecret {
  /** Identifies the row: the stored name, or a generated id for a new secret. */
  key: string;
  name: string;
  /** The secret as stored, or `undefined` for one not saved yet. */
  stored: CodeToolSecretListing | undefined;
  /** The checked domains, which can include some the tool no longer allows. */
  allowedDomains: readonly string[];
  /** The value typed; left empty, a stored secret keeps its value. */
  value: string;
  /** Whether a stored secret's value field is open. A new secret's always is. */
  isReplacingValue: boolean;
  errors: CodeToolFormSecretErrors | undefined;
}

/** Why a secret can't be saved yet, by the field it is about. */
export interface CodeToolFormSecretErrors {
  name?: string;
  value?: string;
  domains?: string;
}

export function buildStoredCodeToolFormSecret(
  stored: CodeToolSecretListing,
): CodeToolFormSecret {
  return {
    key: stored.name,
    name: stored.name,
    stored,
    allowedDomains: stored.allowed_domains,
    value: "",
    isReplacingValue: false,
    errors: undefined,
  };
}

/**
 * A new secret, bound to the tool's domain when it has exactly one. With
 * several, the author picks deliberately rather than sending a secret
 * everywhere by default.
 */
export function buildNewCodeToolFormSecret(
  key: string,
  toolDomains: readonly string[],
): CodeToolFormSecret {
  return {
    key,
    name: "",
    stored: undefined,
    allowedDomains: toolDomains.length === 1 ? toolDomains : [],
    value: "",
    isReplacingValue: false,
    errors: undefined,
  };
}

export function checkCodeToolFormSecretDomainsChanged(
  secret: CodeToolFormSecret,
): boolean {
  if (!secret.stored) return false;
  const storedDomains = new Set(secret.stored.allowed_domains);
  const domains = new Set(secret.allowedDomains);
  return (
    storedDomains.size !== domains.size ||
    [...domains].some((domain) => !storedDomains.has(domain))
  );
}

/**
 * Checks or unchecks one of a secret's domains. The API only takes a stored
 * secret's domains together with its value, so changing them opens the value
 * field.
 */
export function toggleCodeToolFormSecretDomain(
  secret: CodeToolFormSecret,
  domain: string,
  isChecked: boolean,
): CodeToolFormSecret {
  const others = secret.allowedDomains.filter((d) => d !== domain);
  const next: CodeToolFormSecret = {
    ...secret,
    allowedDomains: isChecked ? [...others, domain] : others,
    errors: undefined,
  };
  return checkCodeToolFormSecretDomainsChanged(next)
    ? { ...next, isReplacingValue: true }
    : next;
}

/** A secret bound to domains the tool does not allow, and so cannot use. */
export interface CodeToolSecretOutsideDomains {
  name: string;
  domains: string[];
}

export function findCodeToolSecretsOutsideDomains(
  secrets: readonly CodeToolFormSecret[],
  toolDomains: readonly string[],
): CodeToolSecretOutsideDomains[] {
  const allowed = new Set(toolDomains);
  return secrets.flatMap((secret) => {
    const domains = secret.allowedDomains.filter((d) => !allowed.has(d));
    return domains.length > 0 && secret.name !== ""
      ? [{ name: secret.name, domains }]
      : [];
  });
}

type CodeToolSecretsWriteResult =
  | { isValid: true; write: CodeToolSecretsWrite | undefined }
  | { isValid: false; secrets: CodeToolFormSecret[] };

/**
 * The secrets write for the form's secrets: a stored secret that is gone is
 * deleted, a new one or one with a value entered is set, and an untouched one
 * is left out so it keeps what is stored. Only the tool's own domains are
 * written. Returns the secrets with their errors when any is not ready.
 */
export function buildCodeToolSecretsWrite(
  secrets: readonly CodeToolFormSecret[],
  storedSecrets: readonly CodeToolSecretListing[],
  toolDomains: readonly string[],
): CodeToolSecretsWriteResult {
  const allowed = new Set(toolDomains);
  const write: CodeToolSecretsWrite = {};
  let hasErrors = false;

  const checked = secrets.map((secret) => {
    const errors = findCodeToolFormSecretErrors(secret, secrets, allowed);
    if (errors) {
      hasErrors = true;
      return { ...secret, errors };
    }

    const isRewritten = !secret.stored || secret.value !== "";
    if (isRewritten) {
      write[secret.name] = {
        value: secret.value,
        allowed_domains: secret.allowedDomains.filter((d) => allowed.has(d)),
      };
    }
    return secret;
  });

  if (hasErrors) {
    return { isValid: false, secrets: checked };
  }

  const names = new Set(secrets.map((secret) => secret.name));
  for (const stored of storedSecrets) {
    if (!names.has(stored.name)) {
      write[stored.name] = null;
    }
  }

  return {
    isValid: true,
    write: Object.keys(write).length > 0 ? write : undefined,
  };
}

function findCodeToolFormSecretErrors(
  secret: CodeToolFormSecret,
  secrets: readonly CodeToolFormSecret[],
  toolDomains: ReadonlySet<string>,
): CodeToolFormSecretErrors | undefined {
  if (!secret.stored) {
    const nameResult = codeToolSecretNameSchema.safeParse(secret.name);
    if (!nameResult.success) {
      return {
        name:
          secret.name === ""
            ? "Enter a name."
            : nameResult.error.issues[0]?.message,
      };
    }
    const isDuplicate = secrets.some(
      (other) => other !== secret && other.name === secret.name,
    );
    if (isDuplicate) {
      return { name: "Another secret has this name." };
    }
  }

  if (secret.stored && secret.value === "") {
    return checkCodeToolFormSecretDomainsChanged(secret)
      ? { value: secretDomainChangeNeedsValueMessage }
      : undefined;
  }

  if (
    secret.value.length < codeToolSecretValueMinLength ||
    secret.value.length > codeToolSecretValueMaxLength
  ) {
    return {
      value: `Enter a value of ${codeToolSecretValueMinLength.toString()} to ${codeToolSecretValueMaxLength.toLocaleString()} characters.`,
    };
  }
  if (!secret.allowedDomains.some((domain) => toolDomains.has(domain))) {
    return {
      domains:
        "Choose at least one of the tool's allowed domains to send this secret to.",
    };
  }
  return undefined;
}
